/**
 * Mock 后端的**取数层**（fetch 级路由）。
 *
 * 为什么在 `fetch` 这一层拦，而不是在 `api-client` 里判断：这样切到真实后端时，
 * 上层的请求代码**一个字都不用改**——同样的 URL、同样的请求头、同样的 SSE 帧。
 * 如果 Mock 做在业务层，"Mock 能跑"就完全不能说明"接上后端能跑"。
 *
 * 与真实后端的差异（刻意保留，且只在能暴露问题时保留）：
 * - 数据存在 localStorage（`mock-db`），真实后端在 PostgreSQL；
 * - 令牌**不签名、不校验过期**，但**带着账号身份**：角色门禁（40100 / 40300）按真实语义判，
 *   唯一"假"的地方是令牌本身可以随手伪造——Mock 里谁都能把自己写成 ROOT；
 * - 但**错误码、HTTP 状态、SSE 封帧、`Retry-After` 头全部照搬契约**。
 */

import {
  CreateOrderRequestSchema,
  DEFAULT_PAGE_LIMIT,
  EmailCooldownSeconds,
  EmailExpireSeconds,
  ErrorCode,
  ERROR_MESSAGE,
  IdempotencyKeySchema,
  MAX_PAGE_LIMIT,
  ORDER_TTL_SECONDS,
  PasswordLoginSchema,
  ProfileSchema,
  SendEmailSchema,
  SmsCooldownSeconds,
  SmsExpireSeconds,
  VerifyEmailSchema,
  domainLabel,
  httpStatusOf,
  inferAgeBandFromText,
  isDomain,
  isSuspensionActive,
  nextMonthlyResetAt,
  quotePlanChange,
  startOfUtcMonth,
  type Conversation,
  type Message,
  type Order,
  type Subscription,
} from '@spirit/contracts';

import {
  MOCK_EMAIL_CODE,
  MOCK_PHONE,
  MOCK_SMS_CODE,
  MOCK_USER_ID,
  activateSubscription,
  activeMockChannel,
  currentQuota,
  currentSubscription,
  ensureMockUserByEmail,
  exhaustQuota,
  expireMockOrders,
  findMockOrder,
  findMockUser,
  findMockUserByUsername,
  findPendingMockOrder,
  getMockState,
  isoNow,
  issueMockTokens,
  isUserSuspended,
  nextId,
  nextOrderNo,
  profileOfUser,
  quotaExceededDetails,
  quotaOfUser,
  recordUsage,
  saveMockOrder,
  saveMockState,
  saveProfile,
  subscriptionOfUser,
  touchLastLogin,
  userDtoOf,
  userIdOfAccessToken,
  userIdOfRefreshToken,
  type MockUser,
} from './mock-db';
import { handleAdminRoute } from './mock-admin';
import {
  MOCK_TRIGGER_QUOTA,
  MOCK_TRIGGER_RATE_LIMIT,
  MOCK_TRIGGER_RETRACT,
  MOCK_TRIGGER_UPSTREAM,
  RETRACT_PUBLIC_MESSAGE,
  RISK_PATTERN,
  UPSTREAM_PARTIAL_REPLY,
  pickPlainReply,
} from './mock-replies';
import {
  abortError,
  deltaFrames,
  errorFrame,
  mockSseResponse,
  upstreamFailure,
  type MockSseFrame,
} from './mock-sse';

/**
 * 一次性 40101 开关的存储键。
 *
 * 打开后，**下一次**受保护的请求会强制返回 40101，用来验证"单飞刷新 + 重放一次"
 * 这条链路真的работ——这条链路在正常演示里永远不会被触发，而它恰恰是最容易写错的
 * 地方（并发刷新会让除第一个以外的请求全部被登出）。
 */
const FORCE_40101_KEY = 'spirit.mock.force40101';

const API_PREFIX = /^\/api\/v1/;

function readForced40101(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.localStorage.getItem(FORCE_40101_KEY) === '1';
  } catch {
    return false;
  }
}

function clearForced40101(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(FORCE_40101_KEY);
  } catch {
    // 忽略。
  }
}

/** 供控制台/演示用：打开"下一次请求必 40101"。 */
export function force40101Once(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(FORCE_40101_KEY, '1');
  } catch {
    // 忽略。
  }
}

/* ------------------------------------------------------------------ *
 * 响应构造
 * ------------------------------------------------------------------ */

function jsonResponse(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'Content-Type': 'application/json; charset=utf-8' },
  });
}

function ok<T>(data: T): Response {
  return jsonResponse({ code: 0, message: 'ok', data, requestId: nextId('ckmockreq') });
}

function fail(code: ErrorCode, message?: string, details?: Record<string, unknown>, retryAfterSeconds?: number): Response {
  const headers: Record<string, string> = { 'Content-Type': 'application/json; charset=utf-8' };
  if (retryAfterSeconds !== undefined) {
    headers['Retry-After'] = String(retryAfterSeconds);
  }
  return new Response(
    JSON.stringify({
      code,
      message: message ?? ERROR_MESSAGE[code],
      requestId: nextId('ckmockreq'),
      ...(details === undefined ? {} : { details }),
    }),
    { status: httpStatusOf(code), headers },
  );
}

/* ------------------------------------------------------------------ *
 * 请求解析
 * ------------------------------------------------------------------ */

function readJsonBody(init: RequestInit | undefined): Record<string, unknown> {
  const body = init?.body;
  if (typeof body !== 'string' || body.length === 0) {
    return {};
  }
  try {
    const parsed = JSON.parse(body) as unknown;
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

function readHeader(init: RequestInit | undefined, name: string): string | null {
  const headers = init?.headers;
  if (headers === undefined || headers === null) {
    return null;
  }
  if (headers instanceof Headers) {
    return headers.get(name);
  }
  if (Array.isArray(headers)) {
    const hit = headers.find(([key]) => key.toLowerCase() === name.toLowerCase());
    return hit === undefined ? null : hit[1];
  }
  for (const [key, value] of Object.entries(headers)) {
    if (key.toLowerCase() === name.toLowerCase()) {
      return value;
    }
  }
  return null;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function asNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/**
 * 从 query 里取 `limit`（缺省 → 默认值，夹在 [1, max] 之间）。
 *
 * **不要**写成 `asNumber(Number(search.get('limit')), DEFAULT_PAGE_LIMIT)`：
 * `URLSearchParams.get()` 在没有这个参数时返回 `null`，而 `Number(null)` 是 **0**（有限数），
 * `asNumber` 会照收，于是"没传 limit"变成"只要 1 条"——分页接口悄悄只返回一行。
 * 前端每个调用点都显式传了 limit，所以这个坑只在手写请求（验收脚本、curl）里露出来，
 * 越是这种"只在别人用的时候才炸"的地方越要在取数层统一收口。
 */
function limitOf(search: URLSearchParams, max: number): number {
  const raw = search.get('limit');
  const parsed = raw === null || raw.trim() === '' ? Number.NaN : Number(raw);
  if (!Number.isFinite(parsed)) {
    return Math.min(max, DEFAULT_PAGE_LIMIT);
  }
  return Math.min(max, Math.max(1, Math.trunc(parsed)));
}

function signalOf(init: RequestInit | undefined): AbortSignal {
  return init?.signal ?? new AbortController().signal;
}

/* ------------------------------------------------------------------ *
 * 数据操作
 * ------------------------------------------------------------------ */

function findConversation(id: string): Conversation | null {
  return getMockState().conversations.find((item) => item.id === id) ?? null;
}

/** 追加一条消息，并刷新会话的 `lastMessageAt` / `messageCount`。 */
function appendMessage(message: Message): Message {
  const state = getMockState();
  state.messages.push(message);
  const conversation = state.conversations.find((item) => item.id === message.conversationId);
  if (conversation !== undefined) {
    conversation.messageCount += 1;
    conversation.lastMessageAt = message.createdAt;
  }
  saveMockState();
  return message;
}

/** 会话列表按 `lastMessageAt` 倒序——真实后端就是这么排的。 */
function sortedConversations(): Conversation[] {
  return [...getMockState().conversations].sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt));
}

function pageOf<T>(items: T[], limit: number, cursor: string | null, keyOf: (item: T) => string): {
  items: T[];
  nextCursor: string | null;
} {
  const start = cursor === null ? 0 : Math.max(0, items.findIndex((item) => keyOf(item) === cursor));
  const slice = items.slice(start, start + limit);
  const last = slice[slice.length - 1];
  const nextCursor = last !== undefined && start + limit < items.length ? keyOf(last) : null;
  return { items: slice, nextCursor };
}

/* ------------------------------------------------------------------ *
 * 路由
 * ------------------------------------------------------------------ */

async function route(path: string, search: URLSearchParams, init: RequestInit | undefined): Promise<Response> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const body = readJsonBody(init);
  const authorization = readHeader(init, 'authorization');

  /* ---- 认证 ---- */

  if (path === '/auth/sms/send') {
    return ok({
      expireSeconds: SmsExpireSeconds,
      cooldownSeconds: SmsCooldownSeconds,
      mockCode: MOCK_SMS_CODE,
    });
  }

  if (path === '/auth/sms/verify') {
    const code = asString(body['code']);
    if (code !== MOCK_SMS_CODE) {
      return fail(ErrorCode.INVALID_PARAM, '验证码不正确');
    }
    const user = findMockUser(MOCK_USER_ID);
    if (user === null) {
      return fail(ErrorCode.UNAUTHORIZED);
    }
    // 被封号的用户拿不到令牌。后台刚封掉的人不该还能用手机号登进来——与真实后端
    // auth.service 的"账号状态异常，请联系客服"一致（临时封号到期后这里会自动放行）。
    if (user.status === 'deleted' || isUserSuspended(user, new Date())) {
      return fail(ErrorCode.FORBIDDEN, '账号状态异常，请联系客服');
    }
    touchLastLogin(user.id);
    return ok({
      ...issueMockTokens(user.id),
      user: userDtoOf(user),
      profile: profileOfUser(user.id),
    });
  }

  if (path === '/auth/email/send') {
    /*
     * 与短信分支不同，这里要过一遍 `SendEmailSchema`：手机号在 Mock 里根本不参与查人
     * （谁来都当演示用户），而邮箱**就是**查人的键——格式非法必须在这一步挡住，
     * 否则用户会先看到"验证码已发送"，再卡在永远登不进去的验证码上。
     * 真后端对非法邮箱同样以 40001 拒绝。
     */
    const parsed = SendEmailSchema.safeParse(body);
    if (!parsed.success) {
      return fail(ErrorCode.INVALID_PARAM, '邮箱格式不正确');
    }
    return ok({
      expireSeconds: EmailExpireSeconds,
      cooldownSeconds: EmailCooldownSeconds,
      mockCode: MOCK_EMAIL_CODE,
    });
  }

  if (path === '/auth/email/verify') {
    const parsed = VerifyEmailSchema.safeParse(body);
    if (!parsed.success) {
      return fail(ErrorCode.INVALID_PARAM, '邮箱格式不正确');
    }
    if (parsed.data.code !== MOCK_EMAIL_CODE) {
      return fail(ErrorCode.INVALID_PARAM, '验证码不正确');
    }
    // 未注册的邮箱直接建号：契约与真后端都是"发码 → 登录/注册"一条通道，
    // Mock 只认种子里的演示邮箱的话，新用户注册这条路径在本地永远测不到。
    const user = ensureMockUserByEmail(parsed.data.email, new Date());
    if (user.status === 'deleted' || isUserSuspended(user, new Date())) {
      return fail(ErrorCode.FORBIDDEN, '账号状态异常，请联系客服');
    }
    touchLastLogin(user.id);
    return ok({
      ...issueMockTokens(user.id),
      user: userDtoOf(user),
      profile: profileOfUser(user.id),
    });
  }

  if (path === '/auth/password/login') {
    // 后台账号口令登录。前端 `auth-store.passwordLogin()` 已经先过一遍 `PasswordLoginSchema`
    // 再发请求；这里再校验一次是"取数层照契约收口"的既有做法——手写请求（验收脚本、curl）
    // 绕过前端时也必须被挡住。
    const parsed = PasswordLoginSchema.safeParse(body);
    if (!parsed.success) {
      return fail(ErrorCode.INVALID_PARAM, '请求参数不合法', {
        where: 'body',
        issues: parsed.error.issues.map((issue) => ({ path: issue.path.join('.'), message: issue.message })),
      });
    }

    const account = findMockUserByUsername(parsed.data.username);
    /*
     * 三种失败（账号不存在 / 口令不对 / 不是后台账号）**统一**成同一句话：
     * 分开报就是一台账号枚举机——攻击者能从文案里读出"这个用户名存在"。
     * 真实后端 auth.service 的 eligible 判完也只抛一个 UNAUTHORIZED，理由相同。
     */
    const passwordMatches =
      account !== null &&
      account.role !== 'user' &&
      account.password !== null &&
      account.password === parsed.data.password;
    if (!passwordMatches || account === null) {
      return fail(ErrorCode.UNAUTHORIZED, '账号或密码错误');
    }
    if (account.status === 'deleted' || isUserSuspended(account, new Date())) {
      return fail(ErrorCode.FORBIDDEN, '账号状态异常，请联系客服');
    }
    touchLastLogin(account.id);
    return ok({
      ...issueMockTokens(account.id),
      user: userDtoOf(account),
      // 后台账号没有资料页：契约的 `profile` 可空，这里就得是 null（而不是造一份假的）。
      profile: profileOfUser(account.id),
    });
  }

  if (path === '/auth/refresh') {
    const refreshToken = asString(body['refreshToken']);
    // 刷新也要认出账号：换出来的新令牌必须还是**同一个人**的，
    // 否则管理员点一次"重新登录"就会掉成普通用户。
    const userId = userIdOfRefreshToken(refreshToken);
    if (refreshToken === null || userId === null) {
      return fail(ErrorCode.REFRESH_INVALID);
    }
    return ok(issueMockTokens(userId));
  }

  if (path === '/auth/logout') {
    // 契约只保证信封，`data` 形状不限；这里返回 null 与真实后端一致。
    return ok(null);
  }

  /* ---- 以下都需要登录 ---- */

  if (authorization === null || !/^Bearer[ ]+\S+$/i.test(authorization)) {
    return fail(ErrorCode.UNAUTHORIZED);
  }

  if (readForced40101()) {
    clearForced40101();
    return fail(ErrorCode.TOKEN_EXPIRED);
  }

  /*
   * 解析身份。
   *
   * 令牌不签名、不查过期（Mock 不做真鉴权），但**必须能认出账号**：否则角色门禁只是装饰，
   * `/auth/me` 也没法告诉前端"当前是 admin 还是 root 还是普通用户"。
   * 认不出来的令牌（含形状对但账号不存在的）一律 40100，与"令牌无效"同一个出口，
   * 让 api-client 的单飞刷新链路照常介入。
   */
  const accessToken = authorization.replace(/^Bearer[ ]+/i, '').trim();
  const sessionUserId = userIdOfAccessToken(accessToken);
  const sessionUser = sessionUserId === null ? null : findMockUser(sessionUserId);
  if (sessionUser === null) {
    return fail(ErrorCode.UNAUTHORIZED);
  }
  // 封号（含临时封号未到期）与已删除账号：与 roles.guard 的语义一致。
  // "短期封号到期自动恢复"就落在这里——`isUserSuspended` 到期后自然为 false，不需要定时任务。
  if (sessionUser.status === 'deleted' || isUserSuspended(sessionUser, new Date())) {
    return fail(ErrorCode.FORBIDDEN, '账号状态异常，请联系客服');
  }

  if (path === '/auth/me') {
    return ok({
      user: userDtoOf(sessionUser),
      profile: profileOfUser(sessionUser.id),
      // 管理员 / ROOT 没有个人额度（契约 v1.4.0 允许 `quota: null`）：后台外壳不渲染额度条，
      // 给一个假的 100% 反而会让人以为后台也在消耗额度。
      quota: quotaOfUser(sessionUser.id, new Date()),
    });
  }

  /* ---- 后台（/admin/**）：具体到端点的角色要求由 mock-admin 判 ---- */
  if (path === '/admin' || path.startsWith('/admin/')) {
    const result = handleAdminRoute({ method, path, search, body, session: sessionUser, now: new Date() });
    if (result === null) {
      return fail(ErrorCode.NOT_FOUND, `Mock 未实现该接口：${method} ${path}`);
    }
    return result.ok ? ok(result.data) : fail(result.code, result.message, result.details);
  }

  /* ---- 以下都是用户侧端点：管理员 / ROOT 一律 40300 ---- */
  if (sessionUser.role !== 'user') {
    return fail(ErrorCode.FORBIDDEN, '当前身份无权访问该功能');
  }

  if (path === '/profile') {
    if (method === 'PATCH') {
      /*
       * PATCH 是**局部**更新：没传的字段保留原值。原先的实现"没传就写默认值"，
       * 于是只想改 `childCount` 的一次保存会把 `childAgeBands` 清空——这种丢数据
       * 只有在真机上、用户第二次保存时才被发现。
       */
      const existing = profileOfUser(sessionUser.id);
      const candidate = {
        id: existing?.id ?? nextId('ckmockprofile'),
        userId: sessionUser.id,
        childAgeBands: Array.isArray(body['childAgeBands']) ? body['childAgeBands'] : (existing?.childAgeBands ?? []),
        childCount: asNumber(body['childCount'], existing?.childCount ?? 0),
        concerns: Array.isArray(body['concerns']) ? body['concerns'] : (existing?.concerns ?? []),
        preferredTone: asString(body['preferredTone']) ?? existing?.preferredTone ?? 'gentle',
        extra: {},
      };
      // 过一遍契约再落库：`/auth/me` 下次读的就是这份资料，写脏了会在最远的地方炸。
      const parsedProfile = ProfileSchema.safeParse(candidate);
      if (!parsedProfile.success) {
        return fail(ErrorCode.INVALID_PARAM, '请求参数不合法', {
          where: 'body',
          issues: parsedProfile.error.issues.map((issue) => ({ path: issue.path.join('.'), message: issue.message })),
        });
      }
      saveProfile(sessionUser.id, parsedProfile.data);
      return ok({ profile: parsedProfile.data });
    }
    return ok({ profile: profileOfUser(sessionUser.id) });
  }

  if (path === '/quota') {
    const quota = quotaOfUser(sessionUser.id, new Date());
    if (quota === null) {
      // 到不了这里（上面已经拦掉非普通用户），但让类型收敛成 `Quota` 比断言更诚实。
      return fail(ErrorCode.FORBIDDEN, '当前身份无权访问该功能');
    }
    return ok({ quota });
  }

  if (path === '/quota/ledger') {
    const limit = limitOf(search, MAX_PAGE_LIMIT);
    const cursor = search.get('cursor');
    // 剥掉 Mock 账本里的 `totalTokens`：它只活在"服务端"（聚合要用），
    // 契约的 `LedgerEntry` 里没有它，真后端也不会下发。
    const entries = getMockState().ledger.map(({ id, conversationId, kind, createdAt }) => ({
      id,
      conversationId,
      kind,
      createdAt,
    }));
    return ok(pageOf(entries, limit, cursor, (item) => item.id));
  }

  if (path === '/quota/ledger/conversations') {
    const from = search.get('from');
    const to = search.get('to');
    // 与服务端逐条对齐的两道校验：成对提供、from < to。
    // Mock 只做"够用"的校验是不行的 —— 前端会在 Mock 下开发，宽松的 Mock 会让
    // 非法参数在本地一路绿灯、到真机上才 40001。
    if ((from === null) !== (to === null)) {
      return fail(ErrorCode.INVALID_PARAM, '请求参数不合法', {
        where: 'query',
        issues: [{ path: 'from', message: 'from 与 to 必须同时提供' }],
      });
    }
    const now = new Date();
    const start = from === null ? new Date(startOfUtcMonth(now)) : new Date(from);
    const end = to === null ? new Date(nextMonthlyResetAt(now)) : new Date(to);
    if (!(start.getTime() < end.getTime())) {
      return fail(ErrorCode.INVALID_PARAM, '请求参数不合法', {
        where: 'query',
        issues: [{ path: 'from', message: 'from 必须早于 to' }],
      });
    }
    const limit = limitOf(search, 100);

    const groups = new Map<
      string | null,
      { conversationId: string | null; totalTokens: number; rounds: number; lastAt: string }
    >();
    for (const entry of getMockState().ledger) {
      const at = new Date(entry.createdAt).getTime();
      if (!(at >= start.getTime() && at < end.getTime())) continue;
      const current = groups.get(entry.conversationId);
      if (current === undefined) {
        groups.set(entry.conversationId, {
          conversationId: entry.conversationId,
          totalTokens: entry.totalTokens,
          rounds: 1,
          lastAt: entry.createdAt,
        });
        continue;
      }
      current.totalTokens += entry.totalTokens;
      current.rounds += 1;
      if (entry.createdAt > current.lastAt) current.lastAt = entry.createdAt;
    }

    // 排序规则与服务端 `compareConversationUsage()` 同一套：合计降序 → 最近时间降序 → id 升序
    const sorted = [...groups.values()].sort(
      (a, b) =>
        b.totalTokens - a.totalTokens ||
        b.lastAt.localeCompare(a.lastAt) ||
        (a.conversationId ?? '').localeCompare(b.conversationId ?? ''),
    );

    // 占比的分母是**全部**分组（截断时也仍是窗口真值），否则 Mock 下
    // "各行占比之加 ≠ 100%"会先骗到自己。绝对量到此为止，不再离开这一层。
    const grandTotal = sorted.reduce((sum, group) => sum + group.totalTokens, 0);

    return ok({
      windowStart: start.toISOString(),
      windowEnd: end.toISOString(),
      rounds: sorted.reduce((sum, group) => sum + group.rounds, 0),
      itemCount: sorted.length,
      items: sorted.slice(0, limit).map((group) => ({
        conversationId: group.conversationId,
        rounds: group.rounds,
        // 与服务端一致：`Math.round`，允许各行之和不等于 100（展示值，不是账）。
        sharePercent: grandTotal <= 0 ? 0 : Math.round((group.totalTokens / grandTotal) * 100),
        lastAt: group.lastAt,
      })),
      truncated: sorted.length > limit,
    });
  }

  if (path === '/conversations') {
    const limit = limitOf(search, MAX_PAGE_LIMIT);
    const cursor = search.get('cursor');
    return ok(pageOf(sortedConversations(), limit, cursor, (item) => item.id));
  }

  const conversationMessages = /^\/conversations\/([^/]+)\/messages$/.exec(path);
  if (conversationMessages !== null) {
    const id = decodeURIComponent(conversationMessages[1] ?? '');
    const conversation = findConversation(id);
    if (conversation === null) {
      return fail(ErrorCode.NOT_FOUND, '会话不存在');
    }
    const limit = limitOf(search, MAX_PAGE_LIMIT);
    const cursor = search.get('cursor');
    // **升序**：越翻越新。降序会让"历史消息"第一页就是最新的几条，
    // 用户往上滚却什么也加载不出来。
    const ordered = [...getMockState().messages]
      .filter((item) => item.conversationId === id)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    return ok(pageOf(ordered, limit, cursor, (item) => item.id));
  }

  const conversationById = /^\/conversations\/([^/]+)$/.exec(path);
  if (conversationById !== null) {
    const id = decodeURIComponent(conversationById[1] ?? '');
    const conversation = findConversation(id);
    if (conversation === null) {
      return fail(ErrorCode.NOT_FOUND, '会话不存在');
    }
    if (method === 'DELETE') {
      const state = getMockState();
      state.conversations = state.conversations.filter((item) => item.id !== id);
      state.messages = state.messages.filter((item) => item.conversationId !== id);
      saveMockState();
      return ok(null);
    }
    if (method === 'PATCH') {
      const title = asString(body['title']);
      if (title !== null) {
        conversation.title = title.slice(0, 100);
        saveMockState();
      }
      // 真后端 `PATCH /conversations/:id` 的 `data` 是**裸的** Conversation
      // （conversation.controller.ts 的 updateTitle 返回 `Promise<Conversation>`），
      // Mock 也跟着裸返回，免得前端哪天开始读这个 body 时被包一层骗过去。
      return ok(conversation);
    }
    return ok(conversation);
  }

  if (path === '/chat/completions') {
    return handleChat(init, body, search, sessionUser);
  }

  /* ---- 支付（设计文档 §4.2 的 18-25 号端点里前端真正会调的那 5 个） ---- */

  if (path === '/subscription') {
    return ok({ subscription: subscriptionOfUser(sessionUser.id) });
  }

  if (path === '/orders' && method === 'POST') {
    return handleCreateOrder(init, body, sessionUser.id);
  }

  const orderByNo = /^\/orders\/([^/]+)$/.exec(path);
  if (orderByNo !== null && method === 'GET') {
    return handleReadOrder(decodeURIComponent(orderByNo[1] ?? ''), sessionUser.id);
  }

  const orderClose = /^\/orders\/([^/]+)\/close$/.exec(path);
  if (orderClose !== null && method === 'POST') {
    return handleCloseOrder(decodeURIComponent(orderClose[1] ?? ''), sessionUser.id);
  }

  const mockPayment = /^\/payments\/mock\/([^/]+)$/.exec(path);
  if (mockPayment !== null && method === 'POST') {
    return handleMockPayment(decodeURIComponent(mockPayment[1] ?? ''), sessionUser.id);
  }

  return fail(ErrorCode.NOT_FOUND, `Mock 未实现该接口：${method} ${path}`);
}

/* ------------------------------------------------------------------ *
 * 支付
 * ------------------------------------------------------------------ */

/**
 * 幂等键 → 订单号。
 *
 * 真实后端把 `Idempotency-Key` 存在 Redis 里 24 小时（设计文档 §6）；Mock 用一个
 * 模块级 Map 顶替。它**不跟着 `resetMockState()` 清空**：重置后键会指向一笔已经
 * 不存在的订单，`findMockOrder()` 返回 null 时照常走新建分支，所以不必联动。
 */
const idempotentOrders = new Map<string, string>();

/**
 * 下单。
 *
 * 金额与权益区间**全部**来自 `quotePlanChange()`——与前端预览、与真实后端同一个纯函数。
 * 在 Mock 里另写一套算账逻辑的话，"前端预览 ¥800、Mock 收 ¥800、真后端收 ¥750"
 * 这种偏差会一直被 Mock 掩盖过去。
 */
/**
 * 查单并校验归属。
 *
 * 别人的订单一律当成"不存在"（40400），而不是"无权访问"（40300）：真实后端的资源
 * 查询也是"查不到"，回 40300 等于确认了这个订单号存在，是一条信息泄露。
 * 归属记在 `orderOwners` 上——契约的 `Order` 里没有 userId（用户只看得到自己的单）。
 */
function findOwnedOrder(orderNo: string, userId: string): Order | null {
  const state = getMockState();
  const order = findMockOrder(orderNo);
  if (order === null || state.orderOwners[orderNo] !== userId) {
    return null;
  }
  return order;
}

function handleCreateOrder(init: RequestInit | undefined, body: Record<string, unknown>, userId: string): Response {
  const parsed = CreateOrderRequestSchema.safeParse(body);
  if (!parsed.success) {
    return fail(ErrorCode.INVALID_PARAM, '下单参数不合法（planId / billingCycle / channel）');
  }
  const request = parsed.data;

  // 幂等键是**必填头**：没有它就无法区分"网络重试"和"用户又买了一单"，
  // 而这个区别直接决定用户会不会被扣两次钱。
  const rawKey = readHeader(init, 'idempotency-key');
  const key = rawKey === null ? '' : rawKey.trim();
  if (!IdempotencyKeySchema.safeParse(key).success) {
    return fail(ErrorCode.INVALID_PARAM, '缺少或非法的 Idempotency-Key 请求头（8-128 字符）');
  }

  const replayedOrderNo = idempotentOrders.get(key);
  if (replayedOrderNo !== undefined) {
    const replay = findMockOrder(replayedOrderNo);
    if (replay !== null) {
      return ok({ order: replay });
    }
  }

  const now = new Date();
  expireMockOrders(now);

  const quote = quotePlanChange(currentSubscription(), request.planId, request.billingCycle, now);
  if (!quote.ok) {
    if (quote.reason === 'downgrade-not-allowed') {
      return fail(ErrorCode.CONFLICT, '本期内不支持降级，到期后可重新选择套餐', { reason: quote.reason });
    }
    return fail(ErrorCode.INVALID_PARAM, '升级必须与当前订阅保持同一计费周期', { reason: quote.reason });
  }

  // 同一档位 / 周期 / 渠道还有一笔没过期的待支付订单：复用它，
  // 免得用户手里同时有两个都能付的二维码。
  const reusable = findPendingMockOrder(request.planId, request.billingCycle, request.channel, now);
  if (reusable !== null && reusable.amountFen === quote.quote.amountFen) {
    idempotentOrders.set(key, reusable.orderNo);
    return ok({ order: reusable });
  }

  const orderNo = nextOrderNo();
  const order: Order = {
    orderNo,
    planId: request.planId,
    billingCycle: request.billingCycle,
    kind: quote.quote.kind,
    amountFen: quote.quote.amountFen,
    status: 'pending',
    channel: request.channel,
    // Mock 不生成二维码图片：正好把前端"qrImageDataUrl 为 null 时退化到链接"的分支演示出来。
    qrImageDataUrl: null,
    codeUrl: request.channel === 'wechat' ? `weixin://wxpay/bizpayurl?pr=${orderNo.toLowerCase()}` : null,
    payUrl: request.channel === 'alipay' ? `https://openapi.alipay.com/gateway.do?mock_order=${orderNo}` : null,
    grantStartsAt: quote.quote.startsAt,
    grantExpiresAt: quote.quote.expiresAt,
    payExpiresAt: new Date(now.getTime() + ORDER_TTL_SECONDS * 1_000).toISOString(),
    createdAt: now.toISOString(),
    paidAt: null,
  };

  saveMockOrder(order, userId);
  idempotentOrders.set(key, orderNo);
  return ok({ order });
}

/** 查单：前端「我已完成支付」走这里。 */
function handleReadOrder(orderNo: string, userId: string): Response {
  expireMockOrders(new Date());
  const order = findOwnedOrder(orderNo, userId);
  if (order === null) {
    return fail(ErrorCode.NOT_FOUND, '订单不存在');
  }
  return ok({ order });
}

/** 取消订单：已支付的单不能取消（钱都收了）。 */
function handleCloseOrder(orderNo: string, userId: string): Response {
  expireMockOrders(new Date());
  const order = findOwnedOrder(orderNo, userId);
  if (order === null) {
    return fail(ErrorCode.NOT_FOUND, '订单不存在');
  }
  if (order.status === 'paid') {
    return fail(ErrorCode.CONFLICT, '订单已支付，无法取消');
  }
  if (order.status !== 'pending') {
    return ok({ order });
  }
  const closed: Order = { ...order, status: 'closed' };
  saveMockOrder(closed, userId);
  return ok({ order: closed });
}

/**
 * 模拟支付成功（仅开发环境，真实后端受 `PAY_MOCK` 开关控制）。
 *
 * 开通用的区间取**订单上的快照**，不是"从此刻起算"：用户看到的到期时间与最终拿到的
 * 必须一致，这是一条独立的正确性要求，不是实现细节。
 */
function handleMockPayment(orderNo: string, userId: string): Response {
  expireMockOrders(new Date());
  const order = findOwnedOrder(orderNo, userId);
  if (order === null) {
    return fail(ErrorCode.NOT_FOUND, '订单不存在');
  }
  if (order.status === 'paid') {
    // 幂等：重复点只返回当前状态，不会再开一次通。
    return ok({ order, quota: currentQuota(), subscription: currentSubscription() });
  }
  if (order.status !== 'pending') {
    return fail(ErrorCode.CONFLICT, '订单已关闭，无法支付');
  }

  const paid: Order = { ...order, status: 'paid', paidAt: isoNow() };
  saveMockOrder(paid);

  const subscription: Subscription = {
    planId: paid.planId,
    billingCycle: paid.billingCycle,
    startsAt: paid.grantStartsAt,
    expiresAt: paid.grantExpiresAt,
    orderNo: paid.orderNo,
  };
  // activateSubscription 会同时把 planId 换成新档位：额度快照读的是 planId，
  // 只写 subscription 的话，/quota 还会返回旧套餐的额度。
  activateSubscription(subscription);

  return ok({ order: paid, quota: currentQuota(), subscription });
}

/* ------------------------------------------------------------------ *
 * 聊天（含 SSE）
 * ------------------------------------------------------------------ */

function handleChat(
  init: RequestInit | undefined,
  body: Record<string, unknown>,
  _search: URLSearchParams,
  user: MockUser,
): Response {
  const message = asString(body['message']) ?? '';
  const stream = body['stream'] !== false;
  const requestedDomain = asString(body['domain']);
  let conversationId = asString(body['conversationId']);

  /* 限流：契约要求 42901 一定带 `Retry-After` 头。 */
  if (message.includes(MOCK_TRIGGER_RATE_LIMIT)) {
    return fail(ErrorCode.RATE_LIMITED, undefined, { retryAfterSeconds: 30 }, 30);
  }

  /* 额度：非流式请求直接 42900；流式请求走 error 帧（HTTP 200）。 */
  if (message.includes(MOCK_TRIGGER_QUOTA)) {
    exhaustQuota();
    if (!stream) {
      // details 的口径是契约 v1.3.0 的 `{scope, remainingPercent, resetAt}`。
      return fail(ErrorCode.QUOTA_EXCEEDED, undefined, quotaExceededDetails());
    }
    return mockSseResponse(
      [
        errorFrame({
          code: ErrorCode.QUOTA_EXCEEDED,
          message: ERROR_MESSAGE[ErrorCode.QUOTA_EXCEEDED],
        }),
      ],
      signalOf(init),
    );
  }

  /*
   * 安全拦截。
   * `stream:true`（前端唯一使用的形态）返回 HTTP 200 + error 帧；
   * `stream:false` 才是 HTTP 403 + 40301——服务端刻意用状态码区分
   * "客户端用错了流式开关"和"内容被安全策略拦下"。
   */
  if (RISK_PATTERN.test(message)) {
    const safety = {
      action: 'redirect' as const,
      riskLevel: 'high' as const,
      categories: ['self_harm'],
      reason: '检测到自伤/自杀相关表达',
      publicMessage: '我们很在意你现在的状态，请先联系专业人员获得即时帮助。',
      redirectTo: '12356',
    };
    if (!stream) {
      return fail(ErrorCode.RISK_BLOCKED, undefined, { safety });
    }
    return mockSseResponse(
      [
        errorFrame({
          code: ErrorCode.RISK_BLOCKED,
          message: ERROR_MESSAGE[ErrorCode.RISK_BLOCKED],
          safety,
        }),
      ],
      signalOf(init),
    );
  }

  /* 上游故障：先吐一小段，再断流。验证"已收到的内容不被清空"。 */
  if (message.includes(MOCK_TRIGGER_UPSTREAM)) {
    return mockSseResponse(
      deltaFrames(UPSTREAM_PARTIAL_REPLY),
      signalOf(init),
      upstreamFailure(),
    );
  }

  /* 会话：没有 id 就新建（与真实后端一致：标题由服务端裁剪生成）。 */
  const state = getMockState();
  let conversation = conversationId === null ? null : findConversation(conversationId);
  if (conversation === null && conversationId !== null) {
    return fail(ErrorCode.NOT_FOUND, '会话不存在');
  }
  if (conversation === null) {
    const domain = requestedDomain !== null && isDomain(requestedDomain) ? requestedDomain : 'encyclopedia';
    const ageBand = domain === 'family_education' ? inferAgeBandFromText(message) : null;
    conversation = {
      id: nextId('ckmockconv'),
      // 归属到**当前登录用户**，不是写死演示用户：会话的 userId 与账本的 userId
      // 必须是同一个人，否则后台按用户查会话/用量会各查一半。
      userId: user.id,
      title: message.replace(/\s+/g, ' ').trim().slice(0, 20) || '新会话',
      domain,
      ageBand,
      messageCount: 0,
      lastMessageAt: isoNow(),
      createdAt: isoNow(),
    };
    state.conversations.push(conversation);
    saveMockState();
  }
  conversationId = conversation.id;

  const domain = conversation.domain;
  const ageBand = conversation.ageBand;

  /*
   * 提问的 token 量是 Mock 自己估的：契约已经从消息对象里删掉了 token 字段
   * （前端拿不到绝对量），这里只把它当作"服务端内部真值"用来记账。
   */
  const promptTokens = Math.max(8, Math.ceil(message.length / 2));

  const userMessage = appendMessage({
    id: nextId('ckmockmsg'),
    conversationId,
    role: 'user',
    content: message,
    domain,
    ageBand,
    safetyFlag: 'none',
    latencyMs: 0,
    upstreamModel: null,
    createdAt: isoNow(),
  });

  const reply = `${pickPlainReply(message)}\n\n_（${domainLabel(domain)} · Mock 回复）_`;
  const completionTokens = Math.max(16, Array.from(reply).length);
  const totalTokens = promptTokens + completionTokens;

  /*
   * 撤回场景：回答**照常完整流式下发**，但落库的是公开话术、不计量、不发 usage，
   * 并在 done 之前插一帧 retract。这与服务端 `retractIfUnsafe()` 的行为逐条对齐：
   * 用户在屏幕上先看到完整回答，随后它被整条撤回。
   */
  const retracted = message.includes(MOCK_TRIGGER_RETRACT);

  // 先落助手消息、再记账：账本行要带 `messageId`（后台消息列表按消息显示 token 量），
  // 而消息 id 由 `appendMessage()` 生成。两步都是同步的，先后不影响下发内容。
  const assistantMessage = appendMessage({
    id: nextId('ckmockmsg'),
    conversationId,
    role: 'assistant',
    content: retracted ? RETRACT_PUBLIC_MESSAGE : reply,
    domain,
    ageBand,
    safetyFlag: retracted ? 'blocked' : 'none',
    latencyMs: 1_200,
    upstreamModel: 'deepseek-chat',
    createdAt: isoNow(),
  });

  if (!retracted) {
    recordUsage(conversationId, totalTokens, {
      userId: user.id,
      // 渠道取"当前生效渠道"，一个都没有时是 null（未归因）：后台报表把它单列一组，
      // 而不是丢掉这行——token 已经烧掉了。
      channelId: activeMockChannel()?.id ?? null,
      model: 'deepseek-chat',
      promptTokens,
      completionTokens,
      messageId: assistantMessage.id,
    });
  }

  const frames: MockSseFrame[] = [
    {
      event: 'meta',
      payload: {
        requestId: nextId('ckmockreq'),
        conversationId,
        userMessageId: userMessage.id,
        assistantMessageId: assistantMessage.id,
        domain,
        ageBand,
        routeConfidence: 0.86,
        upstreamModel: 'deepseek-chat',
      },
    },
    ...deltaFrames(reply),
    ...(retracted
      ? ([
          {
            event: 'retract',
            payload: {
              messageId: assistantMessage.id,
              reason: 'AI_MOCK：演示用撤回',
              safety: {
                action: 'block' as const,
                riskLevel: 'high' as const,
                categories: ['self_harm'],
                reason: 'AI_MOCK：演示用撤回',
                publicMessage: RETRACT_PUBLIC_MESSAGE,
                redirectTo: '12356',
              },
            },
          },
        ] satisfies MockSseFrame[])
      : ([
          {
            event: 'usage',
            payload: {
              // v1.3.0：帧里只剩"是不是估算值"与刷新后的额度快照，没有任何 token 量。
              estimated: true,
              // 快照按**当前用户**算：管理员（若能进到这里）没有个人额度，`quotaOfUser` 会返回 null，
              // 于是退回演示额度而不是给一个 `quota: null` 的非法帧。
              quota: quotaOfUser(user.id, new Date()) ?? currentQuota(),
            },
          },
        ] satisfies MockSseFrame[])),
    {
      event: 'done',
      payload: {
        messageId: assistantMessage.id,
        finishReason: retracted ? ('safety_blocked' as const) : ('stop' as const),
        latencyMs: 1_200,
      },
    },
  ];

  return mockSseResponse(frames, signalOf(init));
}

/* ------------------------------------------------------------------ *
 * 入口
 * ------------------------------------------------------------------ */

/**
 * 取代 `fetch` 的 Mock 实现。
 *
 * 非 `/api/v1` 前缀的请求一律**透传**给真实 `fetch`：Mock 只接管契约里的接口，
 * 不能把静态资源、第三方请求也一起吞掉。
 */
export async function mockFetch(url: string, init?: RequestInit): Promise<Response> {
  if (init?.signal?.aborted === true) {
    throw abortError();
  }

  const withoutOrigin = url.replace(/^https?:\/\/[^/]+/i, '');
  const match = API_PREFIX.exec(withoutOrigin);
  if (match === null) {
    return fetch(url, init);
  }

  const rest = withoutOrigin.slice(match[0].length);
  const queryAt = rest.indexOf('?');
  const path = queryAt === -1 ? rest : rest.slice(0, queryAt);
  const search = new URLSearchParams(queryAt === -1 ? '' : rest.slice(queryAt + 1));

  return route(path, search, init);
}

export { force40101Once as forceUnauthorizedOnce };
